import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationDropdown from '@/components/feature/NotificationDropdown';

interface AdminHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

interface ImpersonationBannerProps {
  tenantName: string;
  onExit: () => void;
}

function ImpersonationBanner({ tenantName, onExit }: ImpersonationBannerProps) {
  return (
    <div className="flex items-center justify-between px-4 md:px-8 py-2 bg-violet-600 border-b border-violet-500">
      <div className="flex items-center gap-2.5">
        <span className="w-5 h-5 flex items-center justify-center text-violet-200 shrink-0">
          <i className="ri-spy-line text-sm"></i>
        </span>
        <p className="text-violet-100 text-xs">
          Modo impersonación activo — Estás viendo el Admin de
          <strong className="text-white font-semibold ml-1">{tenantName}</strong>
        </p>
      </div>
      <button
        onClick={onExit}
        className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        <span className="w-3 h-3 flex items-center justify-center">
          <i className="ri-logout-box-line"></i>
        </span>
        Salir de impersonación
      </button>
    </div>
  );
}

export default function AdminHeader({ title, subtitle, actions }: AdminHeaderProps) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { unreadCount } = useNotifications();

  // Impersonation state — read from localStorage
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatedName, setImpersonatedName] = useState('');

  useEffect(() => {
    const tenantId   = localStorage.getItem('impersonated_tenant_id');
    const tenantName = localStorage.getItem('impersonated_tenant_name');
    if (tenantId && tenantName) {
      setImpersonating(true);
      setImpersonatedName(tenantName);
    }
  }, []);

  const handleExitImpersonation = () => {
    localStorage.removeItem('impersonated_tenant_id');
    localStorage.removeItem('impersonated_tenant_name');
    localStorage.removeItem('impersonated_tenant_slug');
    localStorage.removeItem('impersonated_from_sa');
    setImpersonating(false);
    navigate('/sa/tenants');
  };

  return (
    <div className="sticky top-0 z-10">
      {/* Impersonation banner */}
      {impersonating && (
        <ImpersonationBanner
          tenantName={impersonatedName}
          onExit={handleExitImpersonation}
        />
      )}

      {/* Main header */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 md:py-5 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div>
          <h1 className="text-[#0f172a] font-semibold text-lg tracking-tight">{title}</h1>
          {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-3">
          {actions && <div className="flex items-center gap-3">{actions}</div>}

          {/* Bell */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              className="relative w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 transition-colors cursor-pointer"
              title="Notificaciones"
            >
              <i className="ri-notification-3-line text-base"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-400 text-[10px] font-bold text-black flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <NotificationDropdown open={dropdownOpen} onClose={() => setDropdownOpen(false)} />
          </div>
        </div>
      </div>
    </div>
  );
}
